import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { CircleQuestion, Play, ArrowsRotateLeft, Hourglass } from '@gravity-ui/icons';
import {
  getDecodeOptimization,
  updateDecodeOptimization,
  probeDecodeOptimization,
  reprobeDecodeOptimization,
} from '../../services/decodeOptimization';
import {
  MODE_OPTIONS,
  modeLabel,
  isMoreAggressive,
  formatCodec,
  formatFps,
  formatSeconds,
  formatInteger,
  formatDateTime,
  recommendationReasonText,
  fallbackReasonText,
  FALLBACK_REASON_TEXT,
  decodeErrorMessage,
} from './decodeOptimizationUtils';
import { usePolling } from '../../hooks/usePolling';
import { countRu } from '../../utils/pluralRu';

const POLL_INTERVAL_MS = 15000;
const PROBE_DURATION_SECONDS = 20;

const HEADER_HINT =
  'Режим декодирования влияет только на нагрузку CPU при чтении RTSP-потока. ' +
  'Частота детекций задаётся параметром «Интервал кадров (сек)» и не меняется.';

const CADENCE_NOTE =
  'Режим декодирования не изменяет заданную частоту детекций. ' +
  'Детекции по-прежнему выполняются по параметру «Интервал кадров (сек)».';

const CONFLICT_TEXT = 'Настройка изменена в другой сессии. Данные обновлены; повторите действие.';

const RUNTIME_NOT_APPLIED_TEXT =
  'Запрос принят, но runtime пока не применил повторную оптимизацию. ' +
  'Состояние обновится автоматически.';

const Metric = ({ label, value, highlight, note }) => (
  <div className="col">
    <div className="text-muted small text-truncate" title={label}>
      {label}
    </div>
    <div className={highlight ? 'fw-bold' : ''}>{value}</div>
    {note && <div className="text-muted small">{note}</div>}
  </div>
);

const DecodeOptimizationPanel = ({ cameraId, currentFrameInterval, displayTimezone }) => {
  const [state, setState] = useState(null);
  const [draftMode, setDraftMode] = useState('all');
  const [draftDirty, setDraftDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // 'save' | 'probe' | 'reprobe' | null
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const [notice, setNotice] = useState(null);
  const [readOnly, setReadOnly] = useState(false);

  const mountedRef = useRef(true);
  const draftDirtyRef = useRef(false);
  const actionRef = useRef(null);
  const selectId = useId();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    draftDirtyRef.current = draftDirty;
  }, [draftDirty]);

  useEffect(() => {
    actionRef.current = action;
  }, [action]);

  // Полная замена state ответом backend. Ничего не досчитываем на фронте.
  const applyState = useCallback((data, { syncDraft }) => {
    setState(data);
    if (syncDraft) {
      setDraftMode(data?.configured_mode || 'all');
      setDraftDirty(false);
      draftDirtyRef.current = false;
    }
  }, []);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!cameraId) return;
      if (!silent) setLoading(true);
      try {
        const data = await getDecodeOptimization(cameraId);
        if (!mountedRef.current) return;
        // Тихий poll не должен перетирать несохранённый выбор администратора.
        applyState(data, { syncDraft: !silent || !draftDirtyRef.current });
        setReadOnly(false);
        setError(null);
        setErrorKind(null);
      } catch (e) {
        if (!mountedRef.current) return;
        setError(decodeErrorMessage(e, 'load'));
        setErrorKind('load');
        if (e?.response?.status === 404) setReadOnly(true);
      } finally {
        if (mountedRef.current && !silent) setLoading(false);
      }
    },
    [cameraId, applyState]
  );

  useEffect(() => {
    load();
  }, [load]);

  const pollTick = useCallback(() => {
    // Во время probe/PUT/reprobe тик пропускаем, чтобы не перетереть свежий ответ.
    if (actionRef.current) return;
    load({ silent: true });
  }, [load]);

  usePolling(pollTick, POLL_INTERVAL_MS, Boolean(cameraId) && !readOnly);

  const busy = action !== null;
  const disabled = busy || readOnly || !state;

  const intervalDirty =
    Boolean(state) &&
    Number.isFinite(Number(currentFrameInterval)) &&
    Number(currentFrameInterval) !== Number(state.frame_interval_sec);

  const fallbackActive = Boolean(state?.fallback_active);
  const showReprobe = fallbackActive && state?.configured_mode !== 'all';

  const optimizedTarget = draftMode !== 'all';
  const applyDisabled = disabled || (intervalDirty && optimizedTarget);
  const probeDisabled = disabled || intervalDirty;

  const handleModeChange = (e) => {
    setDraftMode(e.target.value);
    setDraftDirty(true);
    draftDirtyRef.current = true;
  };

  const confirmOverride = () => {
    if (!state) return false;
    if (!state.last_probe_at && (draftMode === 'balanced' || draftMode === 'keyframes')) {
      return window.confirm('Рекомендация ещё не рассчитана. Продолжить ручной режим?');
    }
    if (isMoreAggressive(draftMode, state.recommendation_mode)) {
      return window.confirm(
        `Backend рекомендует режим «${modeLabel(state.recommendation_mode)}». ` +
          `Выбранный режим «${modeLabel(draftMode)}» агрессивнее рекомендации. Продолжить?`
      );
    }
    return true;
  };

  const handleApply = async () => {
    if (applyDisabled || !state) return;
    if (!confirmOverride()) return;

    setAction('save');
    actionRef.current = 'save';
    setError(null);
    setErrorKind(null);
    setNotice(null);
    try {
      const data = await updateDecodeOptimization(cameraId, {
        configured_mode: draftMode,
        expected_version: state.version,
      });
      if (!mountedRef.current) return;
      applyState(data, { syncDraft: true });
    } catch (e) {
      if (!mountedRef.current) return;
      if (e?.response?.status === 409) {
        // Optimistic conflict: подтягиваем свежий state, PUT автоматически НЕ повторяем.
        setNotice(CONFLICT_TEXT);
        try {
          const fresh = await getDecodeOptimization(cameraId);
          if (!mountedRef.current) return;
          applyState(fresh, { syncDraft: true });
        } catch (reloadError) {
          if (!mountedRef.current) return;
          setError(decodeErrorMessage(reloadError, 'load'));
          setErrorKind('load');
        }
      } else {
        setError(decodeErrorMessage(e, 'save'));
        setErrorKind('save');
      }
    } finally {
      if (mountedRef.current) setAction(null);
      actionRef.current = null;
    }
  };

  const handleProbe = async () => {
    if (probeDisabled) return;
    setAction('probe');
    actionRef.current = 'probe';
    setError(null);
    setErrorKind(null);
    setNotice(null);
    try {
      const data = await probeDecodeOptimization(cameraId, PROBE_DURATION_SECONDS);
      if (!mountedRef.current) return;
      applyState(data, { syncDraft: false });
    } catch (e) {
      if (!mountedRef.current) return;
      setError(decodeErrorMessage(e, 'probe'));
      setErrorKind('probe');
    } finally {
      if (mountedRef.current) setAction(null);
      actionRef.current = null;
    }
  };

  const handleReprobe = async () => {
    if (probeDisabled) return;
    setAction('reprobe');
    actionRef.current = 'reprobe';
    setError(null);
    setErrorKind(null);
    setNotice(null);
    try {
      const data = await reprobeDecodeOptimization(cameraId, 'admin_retry');
      if (!mountedRef.current) return;
      applyState(data, { syncDraft: false });
      if (data?.runtime_request_applied === false) setNotice(RUNTIME_NOT_APPLIED_TEXT);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(decodeErrorMessage(e, 'reprobe'));
      setErrorKind('reprobe');
    } finally {
      if (mountedRef.current) setAction(null);
      actionRef.current = null;
    }
  };

  const fdt = (value) => formatDateTime(value, displayTimezone);
  const selectedOption = MODE_OPTIONS.find((o) => o.value === draftMode);
  const reasonText = recommendationReasonText(state?.recommendation_reason);

  return (
    <div className="card mb-3" data-testid="decode-optimization-panel">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0 fw-bold">Оптимизация декодирования</h6>
            <span className="text-muted d-inline-flex" title={HEADER_HINT} aria-label={HEADER_HINT}>
              <CircleQuestion width={16} height={16} />
            </span>
          </div>
          <div className="d-flex flex-column align-items-end gap-1">
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                onClick={handleProbe}
                disabled={probeDisabled}
              >
                {action === 'probe' ? <Hourglass width={14} height={14} /> : <Play width={14} height={14} />}
                {action === 'probe' ? 'Проверка потока, около 20 секунд…' : 'Проверить поток и обновить рекомендацию'}
              </button>
              {showReprobe && (
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm d-inline-flex align-items-center gap-1"
                  onClick={handleReprobe}
                  disabled={probeDisabled}
                >
                  {action === 'reprobe' ? (
                    <Hourglass width={14} height={14} />
                  ) : (
                    <ArrowsRotateLeft width={14} height={14} />
                  )}
                  {action === 'reprobe' ? 'Повтор оптимизации…' : 'Повторить оптимизацию'}
                </button>
              )}
            </div>
          </div>
        </div>

        {loading && !state && (
          <div className="d-flex align-items-center gap-2 text-muted small py-2">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            Загрузка настроек декодирования…
          </div>
        )}

        {error && (
          <div className="alert alert-danger py-2 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span>{error}</span>
            {errorKind === 'load' && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => load()}
                disabled={busy}
              >
                Повторить загрузку
              </button>
            )}
          </div>
        )}

        {notice && <div className="alert alert-info py-2 px-3">{notice}</div>}

        {fallbackActive && (
          <div className="alert alert-warning py-2 px-3">
            <div className="fw-bold">Оптимизированное декодирование временно отключено.</div>
            <div>
              Выбран режим «{modeLabel(state.configured_mode)}», работает «{modeLabel(state.effective_mode)}».
            </div>
            <div>{fallbackReasonText(state.fallback_reason)}</div>
            {state.fallback_reason && !FALLBACK_REASON_TEXT[state.fallback_reason] && (
              <div className="text-muted small">Код {state.fallback_reason}</div>
            )}
            <div>Отключено {fdt(state.fallback_at)}</div>
            <div>{countRu(state.auto_failure_count ?? 0, ['неудачная попытка', 'неудачные попытки', 'неудачных попыток'])}</div>
            {state.next_auto_reprobe_at && (
              <div>Следующая автоматическая попытка {fdt(state.next_auto_reprobe_at)}</div>
            )}
          </div>
        )}

        {intervalDirty && (
          <div className="alert alert-warning py-2 px-3">
            Интервал кадров изменён, но ещё не сохранён. Рекомендация рассчитана для{' '}
            {formatInteger(state.frame_interval_sec)} сек. Сначала сохраните камеру, затем повторите проверку
            потока.
          </div>
        )}

        {state && (
          <>
            <div className="row g-3 align-items-start">
              <div className="col-12 col-md-5">
                <label className="form-label mb-1" htmlFor={selectId}>
                  Режим декодирования
                </label>
                <select
                  id={selectId}
                  className="form-select form-select-sm"
                  value={draftMode}
                  onChange={handleModeChange}
                  disabled={disabled}
                >
                  {MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {selectedOption?.hint && <div className="form-text">{selectedOption.hint}</div>}
              </div>

              <div className="col-12 col-md-4 small">
                {/* Подпись отдельной строкой над значением, без двоеточия (docs/STYLE_GUIDE.md). */}
                <div className="mb-1">
                  <div className="text-muted">Выбран</div>
                  <span className="badge text-bg-secondary">{modeLabel(state.configured_mode)}</span>
                </div>
                <div className="mb-1">
                  <div className="text-muted">Работает</div>
                  <span className="badge text-bg-success">{modeLabel(state.effective_mode)}</span>
                </div>
                <div className="mb-1">
                  <div className="text-muted">Рекомендуется</div>
                  <span className="badge text-bg-light border">{modeLabel(state.recommendation_mode)}</span>
                </div>
                {state.recommendation_reason && (
                  <div className="text-muted small">
                    {reasonText || <span>Код причины {state.recommendation_reason}</span>}
                  </div>
                )}
                {!fallbackActive && state.auto_failure_count > 0 && (
                  <div className="text-muted small">
                    {countRu(state.auto_failure_count, ['неудачная попытка', 'неудачные попытки', 'неудачных попыток'])} оптимизации
                  </div>
                )}
              </div>

              <div className="col-12 col-md-3 d-flex align-items-start">
                <button
                  type="button"
                  className="btn btn-primary btn-sm w-100"
                  onClick={handleApply}
                  disabled={applyDisabled}
                >
                  {action === 'save' ? 'Применение…' : 'Применить режим'}
                </button>
              </div>
            </div>

            <div className="row row-cols-2 row-cols-md-4 g-2 mt-2 p-2 bg-light border rounded">
              <Metric label="Кодек" value={formatCodec(state.probe_codec)} />
              <Metric label="FPS" value={formatFps(state.probe_fps)} />
              <Metric label="Keyframe median" value={formatSeconds(state.keyframe_median_seconds)} />
              <Metric
                label="Keyframe p95"
                value={formatSeconds(state.keyframe_p95_seconds)}
                highlight
                note="основной показатель безопасности"
              />
              <Metric label="Keyframe max" value={formatSeconds(state.keyframe_max_seconds)} />
              <Metric label="Интервалов" value={formatInteger(state.probe_keyframe_intervals)} />
              <Metric label="Последняя проверка" value={fdt(state.last_probe_at)} />
              <Metric
                label="Рекомендуемый минимум для интервала кадров"
                value={
                  state.recommended_min_interval_seconds == null
                    ? '—'
                    : `${formatInteger(state.recommended_min_interval_seconds)} сек`
                }
              />
            </div>

            <div className="small text-muted mt-2">
              Рекомендуется режим «{modeLabel(state.recommendation_mode)}»
              {state.recommended_min_interval_seconds != null
                && ` и интервал кадров не меньше ${formatInteger(state.recommended_min_interval_seconds)} сек`}
              . Сейчас интервал {formatInteger(state.frame_interval_sec)} сек.
            </div>

            <div className="alert alert-info py-2 px-3 mt-2 mb-0 small">{CADENCE_NOTE}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default DecodeOptimizationPanel;
